import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ColorPalette, TypographyFamily } from '../../styles/constants';
import {
  Button,
  Typography,
  Paper,
  Box,
  useTheme,
  useMediaQuery,
  Dialog,
  Theme,
  TextField,
  MenuItem,
} from '@mui/material';
import { AppRow } from '../../layout/appRow';
import WestIcon from '@mui/icons-material/West';
import {
  EmployeesSummary,
  MonthSelect,
  ReportFilters,
} from '../../models/reports';
import {
  useAddReportsActivityMutation,
  useDeleteReportsActivityMutation,
  useEditReportsActivityMutation,
  useGetReportsActivitiesQuery,
} from '../../store/reports/reportsActivitiesService';
import { ROLES_ID } from '../../models/employee';
import { ViewMode } from '../../models/preferences';
import { format, formatISO } from 'date-fns';
import {
  formatActivityDate,
  formatDisplayReportsDate,
  getDateByWeekNumber,
  getEndDateAccording,
  getStartDateAccording,
} from '../../services/dateService';
import { ReportsEmployeeWorktime } from './reportsWorktimeDisplay';
import { AppActivityViews } from '../app/appActivityViews';
import { SuccessfulDialog } from '../../models/dialog';
import { Activity } from '../../models/activity';
import useAppSnackbar from '../../hooks/useAppSnackbar';
import { useHandleDisplayDate } from '../../hooks/useHandleDisplayDate';
import { getNextDate, getPreviousDate } from '../../services/reportsService';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { useDisplayDate } from '../../hooks/useDisplayDate';
import { AppConfirmationSuccessful } from '../app/confirmation/appConfirmationSuccessful';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { ActivityForm, ActivityFormData } from '../activity/activityForm';
import { AppConfirmationDelete } from '../app/confirmation/appConfiramationDelete';
import { MyCoolGradient } from '../app/button/myCoolGradient';
import { SmallLayoutRouter } from './reportsPageSmallLayout';
import { AppActivityTableViews } from '../app/appActivityTableViews';
import { viewModeOptions } from '../../constants';
import { convertHoursToMinutes } from '../../services/conversionService';
import { selectEmployeeCardState } from '../../store/reports/reportsSlice';
import { useSelector } from 'react-redux';
import { generateActivityFormData } from '../../services/activityService';
import {
   formatDateOnly,
} from '../../services/dateService';

const makeStyles = (theme: Theme) => ({
  mainContentBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  filters: {
    mb: 2,
  },
  btn: {
    textDecorationColor: ColorPalette.GREEN,
    fontFamily: TypographyFamily.NEXA,
    fontSize: 16,
  },
  employeeBox: {
    p: '20px 15px 20px 15px',
    width: '100%',
  },
  worktimeBox: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  name: {
    color: ColorPalette.RAISIN_BLACK,
    fontWeight: '800',
    fontFamily: TypographyFamily.NEXA,
    fontSize: '24px !important',
    lineHeight: '28.8px',
  },
  text: {
    fontWeight: '400',
    fontSize: '14px !important',
    fontFamily: TypographyFamily.ARIAL,
  },
  currentPeriodRow: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    padding: '20px 0px 20px',
  },
  currentPeriodBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
  },
  customPeriodBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: 2,
    marginTop: 3,
  },

  buttonArrow: {
    color: ColorPalette.DARK_GRAY,
    minWidth: '40px',
  },
  buttonDate: {
    pt: 1.3,
    pr: 0,
    pl: 0,
    ml: 1,
  },
  gradientBtnTypography: { display: 'flex', alignItems: 'center' },
  gradientBtn: {
    fontFamily: TypographyFamily.NEXA,
    textDecorationColor: ColorPalette.GREEN,
    textUnderlineOffset: 2,
    marginLeft: '16px',
  },
  plusSign: {
    fontSize: '25px',
  },
  viewModeMobile: { minWidth: '200px', marginTop: '20px' },
  backBtnContent: { display: 'flex', alignItems: 'center', fontSize: 16 },
  backIcon: {
    mr: 2,
    fill: `url(#my-cool-gradient) ${theme.palette.primary.main}`,
  },
});

interface ReportsEmployeeMobileDetailsProps {
  filters: ReportFilters;
  selectedEmployee: EmployeesSummary;
  setSelectedEmployee: (arg: EmployeesSummary | null) => void;
  reloadEmployeeData: (
    employeeId: string,
    employeeName: string,
    lastLoggedActivity: ActivityFormData | null
  ) => void;
  setRoute: (arg: SmallLayoutRouter) => void;
}

export function ReportsEmployeeMobileDetails({
  filters,
  selectedEmployee,
  setRoute,
  setSelectedEmployee,
  reloadEmployeeData,
}: ReportsEmployeeMobileDetailsProps): JSX.Element {
  const theme = useTheme();
  const styles = makeStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    startDate: startDateFilter,
    endDate: endDateFilter,
    projectId,
  } = filters;

  const { onErrorSnackbar } = useAppSnackbar();

  const { loggedHours, workingHours, employee } = selectedEmployee;
  const { lastLoggedActivity } = useSelector(selectEmployeeCardState);

  const [dateActivity, setDateActivity] = useState<Date>(
    new Date(formatDateOnly(startDateFilter))
  );
  const [viewMode, setViewMode] = useState(ViewMode.OLD_VIEW);
  const [isOpenConfirmation, setIsOpenConfirmation] = useState(false);
  const [isOpenAddActivity, setIsOpenAddActivity] = useState<boolean>(false);
  const [isOpenEditActivity, setIsOpenEditActivity] = useState(false);
  const [successfulDialog, setSuccessfulDialog] = useState<SuccessfulDialog>({
    isOpen: false,
    message: null,
  });

  const [selectedActivity, setSelectedActivity] = useState<ActivityFormData>(
    generateActivityFormData()
  );

  const isCustomDate = useMemo(() => {
    return filters && filters.monthSelect === MonthSelect.CUSTOM;
  }, [filters]);

  const messageConfirmationDelete = useMemo(
    () =>
      `Delete activity ${selectedActivity?.project?.label}) - ${selectedActivity?.task?.name} (${selectedActivity?.hours}h) ?`,
    [selectedActivity]
  );

  const startDate = useMemo(
    () =>
      isCustomDate && viewMode === ViewMode.OLD_VIEW
        ? format(new Date(formatDateOnly(startDateFilter)), 'yyyy-MM-dd')
        : format(getStartDateAccording(viewMode, dateActivity), 'yyyy-MM-dd'),
    [dateActivity, viewMode, isCustomDate, startDateFilter]
  );
  const endDate = useMemo(
    () =>
      isCustomDate && viewMode === ViewMode.OLD_VIEW
        ? format(new Date(formatDateOnly(endDateFilter)), 'yyyy-MM-dd')
        : format(getEndDateAccording(viewMode, dateActivity), 'yyyy-MM-dd'),
    [dateActivity, viewMode, isCustomDate, endDateFilter]
  );

  const startDateFormatted = useMemo(
    () => formatDisplayReportsDate(startDate),
    [startDate]
  );
  const endDateFormatted = useMemo(
    () => formatDisplayReportsDate(endDate),
    [endDate]
  );

  const isAddActivityButtonVisible = useMemo(
    () => viewMode === ViewMode.DAY,
    [viewMode]
  );

  const { displayDate } = useDisplayDate({ viewMode, dateActivity, isMobile });

  const {
    isLoading,
    isFetching,
    data: activities = [],
  } = useGetReportsActivitiesQuery({
    startDate,
    endDate,
    employeeId: selectedEmployee?.employee.id,
    roleId: ROLES_ID.TEAM_LEAD,
    projectId: projectId,
  });

  const [addActivity, { isLoading: isLoadingAddActivity }] =
    useAddReportsActivityMutation();
  const [editActivity, { isLoading: isLoadingEditActivity }] =
    useEditReportsActivityMutation();
  const [deleteActivity, { isLoading: isLoadingDeleteActivity }] =
    useDeleteReportsActivityMutation();

  const isLoadingAll = useMemo(
    () =>
      isLoading ||
      isFetching ||
      isLoadingAddActivity ||
      isLoadingEditActivity ||
      isLoadingDeleteActivity,
    [
      isFetching,
      isLoading,
      isLoadingAddActivity,
      isLoadingDeleteActivity,
      isLoadingEditActivity,
    ]
  );

  useEffect(() => {
    setSelectedActivity({
      id: lastLoggedActivity?.id ? lastLoggedActivity.id : '',
      project: lastLoggedActivity?.project ? lastLoggedActivity.project : null,
      task: lastLoggedActivity?.task ? lastLoggedActivity.task : null,
      date: formatISO(dateActivity),
      hours: lastLoggedActivity?.hours ? lastLoggedActivity.hours : '',
      comment: lastLoggedActivity?.comment ? lastLoggedActivity.comment : '',
      ticketIdentification: lastLoggedActivity?.ticketIdentification
        ? lastLoggedActivity.ticketIdentification
        : '',
      isAMS: lastLoggedActivity?.isAMS || false,
    });
  }, [dateActivity, lastLoggedActivity]);

  const onDayActivities = useCallback((activityDate: string) => {
    setDateActivity(new Date(formatDateOnly(activityDate)));
    setViewMode(ViewMode.DAY);
  }, []);

  const handleOpenAddActivity = useCallback(() => {
    setIsOpenAddActivity((prev) => !prev);
  }, []);

  const handleOpenEditActivity = useCallback(() => {
    setIsOpenEditActivity((prev) => !prev);
  }, []);

  const onCloseDeleteDialog = useCallback(() => {
    setIsOpenConfirmation(false);
    setSelectedActivity(generateActivityFormData());
  }, []);

  const onCloseConfirmDialog = useCallback(() => {
    setSuccessfulDialog({
      isOpen: false,
      message: null,
    });
    setSelectedActivity(generateActivityFormData());
  }, []);

  const onDeleteActivity = useCallback((activity: Activity) => {
    setSelectedActivity(generateActivityFormData(activity));
    setIsOpenConfirmation(true);
  }, []);

  const onEditActivity = useCallback((activity: Activity) => {
    setSelectedActivity(generateActivityFormData(activity));
    setIsOpenEditActivity(true);
  }, []);

  const onWeekActivities = useCallback(
    (weekNumber: number) => {
      setDateActivity(getDateByWeekNumber(weekNumber, dateActivity));
      setViewMode(ViewMode.WORK_WEEK);
    },
    [dateActivity]
  );

  const onSubmitAddActivity = useCallback(
    (formData: ActivityFormData<Date>) => {
      const payload = {
        employeeId: employee.id,
        comment: formData.comment,
        date: formatActivityDate(formData.date),
        minutes: convertHoursToMinutes(Number(formData.hours)),
        taskId: formData.task?.id || '',
        ticketIdentification: formData.ticketIdentification || '',
        isAMS: formData?.isAMS || false,
      };

      addActivity(payload)
        .then(() => {
          handleOpenAddActivity();
          reloadEmployeeData(employee.id, employee.name, {
            id: formData.id,
            project: formData.project,
            task: formData.task,
            hours: formData.hours,
            comment: formData.comment,
            date: formatISO(formData.date),
            ticketIdentification: formData.ticketIdentification,
            isAMS: formData?.isAMS || false,
          });
        })
        .catch(onErrorSnackbar);
    },
    [
      addActivity,
      employee.id,
      employee.name,
      handleOpenAddActivity,
      onErrorSnackbar,
      reloadEmployeeData,
    ]
  );

  const onSubmitEditActivity = useCallback(
    (formData: ActivityFormData<Date>) => {
      if (selectedActivity) {
        const payload = {
          comment: formData.comment,
          date: formatActivityDate(formData.date),
          minutes: convertHoursToMinutes(Number(formData.hours)),
          taskId: formData.task?.id || '',
          employeeId: employee.id,          
          ticketIdentification: formData.ticketIdentification || '',
          isAMS: formData?.isAMS || false,
        };

        editActivity({
          idActivity: selectedActivity.id,
          editActivityInfo: payload,
        })
          .then(() => {
            setIsOpenEditActivity(false);
            setSelectedActivity(generateActivityFormData());
            reloadEmployeeData(employee.id, employee.name, lastLoggedActivity);
          })
          .catch(onErrorSnackbar);
      }
    },
    [
      selectedActivity,
      employee.id,
      employee.name,
      editActivity,
      onErrorSnackbar,
      reloadEmployeeData,
      lastLoggedActivity,
    ]
  );

  const onConfirmDeleteActivity = useCallback(() => {
    if (!selectedActivity) {
      return;
    }
    deleteActivity(selectedActivity.id)
      .then(() => {
        setIsOpenConfirmation(false);
        setSuccessfulDialog({
          isOpen: true,
          message: 'Successfully deleted',
        });
      })
      .then(() => {
        setSelectedActivity(generateActivityFormData());

        let lastActivity = lastLoggedActivity;

        if (
          lastLoggedActivity &&
          lastLoggedActivity.project?.id === selectedActivity.project?.id &&
          lastLoggedActivity.task?.id === selectedActivity.task?.id &&
          lastLoggedActivity.hours === selectedActivity.hours &&
          lastLoggedActivity.comment === selectedActivity.comment
        ) {
          lastActivity = null;
        }

        reloadEmployeeData(employee.id, employee.name, lastActivity);

        setTimeout(() => {
          setSuccessfulDialog({
            isOpen: false,
            message: null,
          });
        }, 2000);
      })
      .catch(onErrorSnackbar);
  }, [
    deleteActivity,
    employee.id,
    employee.name,
    lastLoggedActivity,
    onErrorSnackbar,
    reloadEmployeeData,
    selectedActivity,
  ]);

  const handleChangeViewMode = useCallback(
    (viewModeArg: ViewMode) => setViewMode(viewModeArg),
    []
  );

  const handleChangeMobileViewMode = useCallback((event) => {
    setViewMode(event.target.value);
  }, []);

  const { handleDisplayDate } = useHandleDisplayDate({
    viewMode,
    handleChangeViewMode,
  });

  const handlePreviousDate = useCallback(() => {
    setDateActivity(getPreviousDate(dateActivity, viewMode));
  }, [dateActivity, setDateActivity, viewMode]);

  const handleNextDate = useCallback(() => {
    setDateActivity(getNextDate(dateActivity, viewMode));
  }, [dateActivity, setDateActivity, viewMode]);

  const onAddActivity = useCallback(
    (addDate: string) => {
      setDateActivity(new Date(formatDateOnly(addDate)));
      setSelectedActivity((prevState) => ({
        ...prevState,
        date: addDate,
      }));
      handleOpenAddActivity();
    },
    [handleOpenAddActivity]
  );

  return (
    <>
      <AppRow sx={styles.filters}>
        <Button
          onClick={() => {
            setRoute(SmallLayoutRouter.RESULTS);
            setSelectedEmployee(null);
          }}
        >
          <Typography variant="h2" sx={styles.backBtnContent}>
            <MyCoolGradient />
            <WestIcon sx={styles.backIcon} />
            <u style={styles.btn}>Back</u>
          </Typography>
        </Button>
        <Button
          onClick={() => {
            setRoute(SmallLayoutRouter.FILTERS);
            setSelectedEmployee(null);
          }}
        >
          <Typography variant="h2">
            <u style={styles.btn}>Filters page</u>
          </Typography>
        </Button>
      </AppRow>
      <Box sx={styles.mainContentBox}>
        <Paper sx={styles.employeeBox}>
          <Typography sx={styles.name}>{employee.name}</Typography>
          <Typography sx={styles.text}>{employee.jobTitle}</Typography>
          <Box sx={styles.worktimeBox}>
            <Typography
              sx={styles.text}
            >{`ID: ${employee.internalId}`}</Typography>
            <Box>
              <ReportsEmployeeWorktime
                loggedHours={loggedHours}
                workingHours={workingHours}
                projectFilter={filters.projectId}
                sx={{ fontSize: '14px !important', mr: 0 }}
                showLogged
              />
            </Box>
          </Box>
        </Paper>

        {isMobile && (
          <TextField
            select
            fullWidth
            label="View mode"
            sx={styles.viewModeMobile}
            value={viewMode}
            onChange={handleChangeMobileViewMode}
          >
            {viewModeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        {isCustomDate ? (
          <Box sx={styles.customPeriodBox}>
            <Typography variant="h1">{startDateFormatted}&nbsp;-</Typography>
            <Typography variant="h1">&nbsp;{endDateFormatted}</Typography>
          </Box>
        ) : (
          <AppRow sx={styles.currentPeriodRow}>
            <Box sx={styles.currentPeriodBox}>
              <Button sx={styles.buttonArrow} onClick={handlePreviousDate}>
                <ArrowBackIosRoundedIcon
                  fontSize={isMobile ? 'small' : 'medium'}
                />
              </Button>
              <Button sx={styles.buttonArrow} onClick={handleNextDate}>
                <ArrowForwardIosRoundedIcon
                  fontSize={isMobile ? 'small' : 'medium'}
                />
              </Button>
              <Button sx={styles.buttonDate} onClick={handleDisplayDate}>
                <Typography
                  variant="h1"
                  sx={{
                    ...(isMobile && { fontSize: '16px !important' }),
                    verticalAlign: 'middle',
                  }}
                >
                  {displayDate}
                </Typography>
              </Button>
            </Box>
            {isAddActivityButtonVisible && (
              <Button onClick={handleOpenAddActivity}>
                <Typography
                  variant="h2"
                  fontSize={16}
                  sx={styles.gradientBtnTypography}
                >
                  <span style={styles.plusSign}>+</span>
                </Typography>
              </Button>
            )}
          </AppRow>
        )}

        {viewMode === ViewMode.OLD_VIEW ? (
          <AppActivityTableViews
            startDate={startDate}
            endDate={endDate}
            isEmployeeSection
            location={employee.location}
            activities={activities}
            employeeWorkHours={employee.workingHours}
            onEditActivity={onEditActivity}
            onDeleteActivity={onDeleteActivity}
            onAddActivity={onAddActivity}
          />
        ) : (
          <AppActivityViews
            activities={activities}
            dateActivity={dateActivity}
            location={employee.location}
            viewMode={viewMode}
            isLoading={isLoadingAll}
            onDayActivities={onDayActivities}
            onDeleteActivity={onDeleteActivity}
            onEditActivity={onEditActivity}
            onWeekActivities={onWeekActivities}
            showOnlyloggedTime={!!filters.projectId}
            employeeWorkHours={employee.workingHours}
          />
        )}
      </Box>

      <AppConfirmationSuccessful
        isOpen={successfulDialog.isOpen}
        message={successfulDialog.message}
        onClose={onCloseConfirmDialog}
      />

      <Dialog
        open={isOpenAddActivity}
        sx={{ borderRadius: '5px' }}
        PaperProps={{ sx: { m: 1 } }}
      >
        <AppDialogTitle
          title="Add activity"
          handleClose={handleOpenAddActivity}
        />
        <ActivityForm
          onClose={handleOpenAddActivity}
          idEmployee={employee.id}
          activity={selectedActivity}
          workingHours={selectedEmployee.employee.workingHours}
          onSubmit={onSubmitAddActivity}
          isLoading={isLoadingAll}
        />
      </Dialog>

      <Dialog
        open={isOpenEditActivity}
        sx={{ borderRadius: '5px' }}
        PaperProps={{ sx: { m: 1 } }}
      >
        <AppDialogTitle
          title="Edit activity"
          handleClose={handleOpenEditActivity}
        />
        <ActivityForm
          onClose={handleOpenEditActivity}
          idEmployee={employee.id}
          workingHours={selectedEmployee.employee.workingHours}
          onSubmit={onSubmitEditActivity}
          activity={selectedActivity}
          isLoading={isLoadingAll}
        />
      </Dialog>

      <AppConfirmationDelete
        isOpen={isOpenConfirmation}
        message={messageConfirmationDelete}
        onClose={onCloseDeleteDialog}
        onConfirm={onConfirmDeleteActivity}
      />
    </>
  );
}
